/* eslint-disable jira/ff/unsafe-no-exposure */
import type { ProjectType } from '@jira/platform__project-types/src/project-types.tsx';
import { UNSAFE_noExposureExp } from '@atlassian/jira-feature-experiments';
import { fetchSiteTraitsResource } from '@atlassian/jira-router-resources-recommendations/src/services/fetch-site-traits/index.tsx';
import { useResource } from '@atlassian/react-resource-router';
import { useBasicEligibilityCriteriaCheck } from '../use-eligibility-criteria/index.tsx';
import type { Banners } from '../../common/constants.tsx';

export type TraitData = {
	name: string;
	value: string | boolean | number;
} | null;

const jiraTrait = 'upsell_jsw_tenants_gte_80_percent_automation_usage';
const jsmTrait = 'upsell_jsm_tenants_gte_80_percent_automation_usage';

const jiraLimit = 1700;
const jsmLimit = 5000;

const getProductSpecificTrait = (
	siteTraitsData: TraitData[] | null,
	productType: ProjectType | undefined,
) => {
	return siteTraitsData && productType
		? siteTraitsData.find((trait) => {
				if (!trait) return;

				switch (productType) {
					case 'software':
						return trait.name === jiraTrait;
					case 'service_desk':
						return trait.name === jsmTrait;
					default:
						return false;
				}
			})
		: undefined;
};

export const convertTraitsToBannerType = (
	limit: number,
	automationCount: number | undefined,
): Banners | undefined => {
	if (automationCount) {
		const percentage = (automationCount / limit) * 100;

		if (percentage >= 80 && percentage < 90) {
			return '80_90';
		}
		if (percentage >= 90 && percentage < 100) {
			return '90_100';
		}
		if (percentage >= 100) {
			return 'gte100';
		}
	}

	return undefined;
};

export const useStandardAutomationLimitAwarenessExperiment = () => {
	const { isEligible, projectType } = useBasicEligibilityCriteriaCheck();
	const { data: siteTraitsData } = useResource(fetchSiteTraitsResource);
	const [expConfig, fireStatsigExposure] = UNSAFE_noExposureExp(
		'upsell_jira_global_automations_limit_banner',
	);

	const productTrait = getProductSpecificTrait(siteTraitsData, projectType);

	const cohort = expConfig.get('cohort', 'not-enrolled');

	const automationsLimit = projectType === 'software' ? jiraLimit : jsmLimit;
	const automationsUsedCount = productTrait?.value ? Number(productTrait?.value) : undefined;

	const bannerType = productTrait?.value
		? convertTraitsToBannerType(automationsLimit, automationsUsedCount)
		: undefined;

	return {
		isEligible,
		cohort,
		projectType,
		bannerType,
		automationsUsedCount,
		automationsLimit,
		fireStatsigExposure,
	};
};
